import { Box, type BoxRef } from '../../../../../../mads';
import { ScrollYPosition } from '../../../../../../shared/util/scrollPosition';
import { IsStickyContext, useIsSticky } from '../../../../util/useIsSticky';

import React, { type PropsWithChildren, useCallback, useState, useRef, useEffect } from 'react';
import { Animated, type DimensionValue, StyleSheet, type LayoutChangeEvent } from 'react-native';

const useIntersectionObserver = () => {
    const elementRef = useRef<BoxRef>(null);
    const [isIntersecting, setIsIntersecting] = useState(false);

    useEffect(() => {
        const element = elementRef.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            ([entry]: { isIntersecting: boolean }[]) => {
                setIsIntersecting(entry.isIntersecting);
            },
            { threshold: 1 },
        );

        observer.observe(element as unknown as Element);

        return () => {
            observer.unobserve(element as unknown as Element);
        };
    }, []);

    return [elementRef, isIntersecting] as const;
};

export type StickyButtonsRootProps = PropsWithChildren<{
    needSticky?: boolean;
}>;

export const StickyButtonsRoot = (props: StickyButtonsRootProps) => {
    const scrollYRef = useRef(new Animated.Value(0));

    useEffect(() => {
        const handleScroll = () => {
            scrollYRef.current.setValue(window.scrollY);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <ScrollYPosition.Provider value={scrollYRef.current}>
            <IsStickyContext.Provider value={props.needSticky ?? true}>
                {props.children}
            </IsStickyContext.Provider>
        </ScrollYPosition.Provider>
    );
};

export type StickyButtonProps = PropsWithChildren<{
    /**
     * Whether the background area of the sticky button should be filled with a colour.
     *
     * i.e. the sticky button should be filled with white to hide the content behind it.
     */
    stickyAreaBackgroundColor?: string;
}>;

/**
 * The component that should wrap the button that you want to be sticky.
 */
export const StickyButton = (props: StickyButtonProps) => {
    const needSticky = useIsSticky();
    const [stickyButtonHeight, setStickyButtonHeight] = useState<number | null>(null);
    const [placeholderRef, isPlaceholderIntersecting] = useIntersectionObserver();

    const handleLayout = useCallback((layoutEvent: LayoutChangeEvent) => {
        setStickyButtonHeight(layoutEvent.nativeEvent.layout.height);
    }, []);

    // Desktop layout should always be sticky, rather than dropping inline when spot in document flow reached
    const shouldBeSticky = needSticky && !isPlaceholderIntersecting;

    return (
        <>
            <Box
                sx={[
                    {
                        // On iOS devices using chrome browser, this prevents the Apply CTA from being clipped behind the safe area.
                        paddingBottom: 'env(safe-area-inset-bottom, 0px)' as DimensionValue,
                    },
                    shouldBeSticky && styles.stickyButton,
                    !!props?.stickyAreaBackgroundColor && {
                        backgroundColor: props.stickyAreaBackgroundColor,
                    },
                ]}
                onLayout={handleLayout}
            >
                {props.children}
            </Box>
            <Box ref={placeholderRef} sx={[{ height: shouldBeSticky ? stickyButtonHeight : 0 }]} />
        </>
    );
};

export const StickyButtonAccessory = (_props: PropsWithChildren) => {
    return null;
};

const styles = StyleSheet.create({
    stickyButton: {
        // @ts-expect-error - position 'fixed' is valid on web (react-native-web)
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        elevation: 3,
        zIndex: 3,
        paddingBottom: 8,
    },
});
